import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { possiblePhoneValidator, toE164 } from '../../../../../../../../../shared/helpers/phone-number';
import * as fromRoot from '../../../../../../../../../state/app.state';
import {
  FetchProjectRolesListRequest,
  FetchExistingMembersListRequest,
  InviteUserRequest,
  UpdateUserRequest,
  DeleteUserRequest,
  FetchProjectSitesForInvitationRequest,
  FetchProjectMembersListRequest,
  CheckEmailExistsRequest,
  CheckPhoneExistsRequest,
  ClearEmailExistsCheck,
  ClearPhoneExistsCheck,
} from '../../../../../../../core/projects.actions';
import {
  projectRolesListInfo,
  existingMembersListInfo,
  projectSitesForInvitation,
  emailExistsCheck,
  phoneExistsCheck,
  projectMembersListInfo,
} from '../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';

@Component({
    selector: 'app-create-or-edit-user',
    templateUrl: './create-or-edit-user.component.html',
    styleUrls: ['./create-or-edit-user.component.scss'],
    standalone: false
})
export class CreateOrEditUserComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  form = new FormGroup({
    firstName: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    lastName: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    email: new FormControl(
      { value: null, disabled: false },
      {
        validators: [Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)],
      }
    ),
    phone: new FormControl({ value: null, disabled: false }, { validators: [possiblePhoneValidator] }),
    role: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    reportsTo: new FormControl({ value: null, disabled: false }),
    selectedUser: new FormControl({ value: null, disabled: false }),
  });

  errorMessages: any = {};
  errorMessageMap = {
    firstName: {
      required: 'This field is required.',
    },
    lastName: {
      required: 'This field is required.',
    },
    email: {
      pattern: 'Please enter a valid email address.',
      emailExists: 'This email already exists.',
      emailOrPhoneRequired: 'Email or phone is required.',
    },
    phone: {
      required: 'This field is required.',
      possiblePhone: 'Please enter a valid 10-digit phone number.',
      phoneExists: 'This phone number already exists.',
      emailOrPhoneRequired: 'Email or phone is required.',
    },
    role: {
      required: 'This field is required.',
    },
    reportsTo: {},
    selectedUser: {
      required: 'This field is required.',
    },
    sites: {
      required: 'This field is required.',
    },
  };
  isFormDirty = false;
  projectId: any;
  rolesOptions = [];
  projectMembers = [];
  reportsToOptions = [];
  isDwrReportingHierarchyEnabled = false;
  isOrgAdmin = false;
  manageableOrgRoles: string[] | null = [];
  isReadOnly = false;
  userOptions = [];
  sitesOptions = [];
  selectedUserData: any = null;
  isAddNewMode = false;
  private originalEmail: string | null = null;
  private originalPhone: string | null = null;
  private lastCheckedEmail: string | null = null;
  private lastCheckedPhone: string | null = null;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private dialog: MatDialog,
    private actions$: Actions,
    private cdRef: ChangeDetectorRef
  ) {}

  isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  ngOnInit(): void {
    this.store.dispatch(ClearEmailExistsCheck());
    this.store.dispatch(ClearPhoneExistsCheck());

    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    } else {
      const url = this.router.url;
      const path = url.split('?')[0];
      const segments = path.split('/');
      this.projectId = segments[segments.length - 1];
    }

    if (!this.projectId && this.data?.projectId) {
      this.projectId = this.data?.projectId;
    }

    this.isDwrReportingHierarchyEnabled = this.data?.dwrReportingHierarchyEnabled === true;
    this.isOrgAdmin = this.data?.isOrgAdmin === true;
    this.manageableOrgRoles = this.isOrgAdmin ? null : this.data?.manageableOrgRoles || [];
    this.isReadOnly = this.data?.readOnly === true;
    if (!this.isDwrReportingHierarchyEnabled) {
      this.form.controls['reportsTo'].setValue(null, { emitEvent: false });
    }

    if (this.projectId) {
      this.fetchProjectRolesList();
      this.fetchProjectMembersList();
      if (!(this.data?.id || this.data?.uuid)) {
        this.fetchUsersList();
      }
    }

    if (this.data?.id || this.data?.first_name || this.data?.last_name || this.data?.email) {
      const firstName = this.data?.first_name || this.data?.firstName || null;
      const lastName = this.data?.last_name || this.data?.lastName || null;
      const roleCode =
        this.data?.role_code ||
        this.data?.roleCode ||
        this.data?.code ||
        this.data?.role ||
        this.data?.roleName ||
        this.data?.role_name;

      const phoneValue = this.data?.phone || null;

      this.originalEmail = this.data?.email || null;
      this.originalPhone = phoneValue || null;

      this.form.patchValue({
        firstName: firstName,
        lastName: lastName,
        email: this.data?.email,
        phone: phoneValue,
        role: roleCode,
        reportsTo: this.isDwrReportingHierarchyEnabled
          ? this.data?.reports_to?.uuid || this.data?.reports_to_account_id || null
          : null,
      });
    }

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });

    if (!(this.data?.id || this.data?.uuid)) {
      this.form.controls['selectedUser'].setValidators([Validators.required]);
      this.form.controls['firstName'].clearValidators();
      this.form.controls['lastName'].clearValidators();
      this.form.controls['email'].clearValidators();
      this.form.controls['phone'].clearValidators();
      this.form.controls['selectedUser'].updateValueAndValidity({ emitEvent: false });
      this.form.controls['firstName'].updateValueAndValidity({ emitEvent: false });
      this.form.controls['lastName'].updateValueAndValidity({ emitEvent: false });
      this.form.controls['email'].updateValueAndValidity({ emitEvent: false });
      this.form.controls['phone'].updateValueAndValidity({ emitEvent: false });
    }

    this.form.controls['email'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((email: string) => {
      if (this.selectedUserData) {
        return;
      }
      // Clear cross-field error on phone when user types in email
      if (email && this.form.controls['phone'].hasError('emailOrPhoneRequired')) {
        this.form.controls['phone'].setErrors(null);
        this.checkForErrors('phone');
      }
      if (!email) {
        return;
      }

      const trimmedEmail = email.trim();
      const isValid = this.isValidEmail(trimmedEmail);

      if (!isValid) {
        return;
      }

      if (this.originalEmail && trimmedEmail === this.originalEmail) {
        if (this.form.controls['email'].hasError('emailExists')) {
          const errors = { ...this.form.controls['email'].errors };
          delete errors['emailExists'];
          this.form.controls['email'].setErrors(Object.keys(errors).length > 0 ? errors : null);
          this.checkForErrors('email');
          this.cdRef.detectChanges();
        }
      }
    });

    this.store
      .select(emailExistsCheck)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (!data) {
          return;
        }

        const currentEmail = this.form.controls['email'].value?.trim();

        if (currentEmail === data?.email && currentEmail !== this.originalEmail) {
          if (data?.exists === true) {
            this.form.controls['email'].markAsTouched();
            this.form.controls['email'].setErrors({ emailExists: true });
            this.checkForErrors('email');
            this.cdRef.detectChanges();
          } else if (data?.exists === false) {
            if (this.form.controls['email'].hasError('emailExists')) {
              const errors = { ...this.form.controls['email'].errors };
              delete errors['emailExists'];
              this.form.controls['email'].setErrors(Object.keys(errors).length > 0 ? errors : null);
              this.checkForErrors('email');
              this.cdRef.detectChanges();
            }
          }
        }
      });

    this.form.controls['phone'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((phone: string) => {
      if (this.selectedUserData) {
        return;
      }
      // Clear cross-field error on email when user types in phone
      if (phone && this.form.controls['email'].hasError('emailOrPhoneRequired')) {
        this.form.controls['email'].setErrors(null);
        this.checkForErrors('email');
      }
      if (!phone) {
        return;
      }

      const trimmedPhone = phone.trim();

      if (this.originalPhone && trimmedPhone === this.originalPhone) {
        if (this.form.controls['phone'].hasError('phoneExists')) {
          const errors = { ...this.form.controls['phone'].errors };
          delete errors['phoneExists'];
          this.form.controls['phone'].setErrors(Object.keys(errors).length > 0 ? errors : null);
          this.checkForErrors('phone');
          this.cdRef.detectChanges();
        }
      }
    });

    this.store
      .select(phoneExistsCheck)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (!data) {
          return;
        }

        const currentPhone = this.form.controls['phone'].value?.trim();
        let formattedPhone = currentPhone || '';
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = '+1' + formattedPhone;
        }

        if (formattedPhone === data?.phone && formattedPhone !== this.originalPhone) {
          if (data?.exists === true) {
            this.form.controls['phone'].markAsTouched();
            this.form.controls['phone'].setErrors({ phoneExists: true });
            this.checkForErrors('phone');
            this.cdRef.detectChanges();
          } else if (data?.exists === false) {
            if (this.form.controls['phone'].hasError('phoneExists')) {
              const errors = { ...this.form.controls['phone'].errors };
              delete errors['phoneExists'];
              this.form.controls['phone'].setErrors(Object.keys(errors).length > 0 ? errors : null);
              this.checkForErrors('phone');
              this.cdRef.detectChanges();
            }
          }
        }
      });

    this.form.controls['selectedUser'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(userId => {
      if (userId === 'ADD_NEW') {
        this.isAddNewMode = true;
        this.selectedUserData = null;
        this.form.patchValue(
          { firstName: null, lastName: null, email: null, phone: null },
          { emitEvent: false }
        );
        this.form.controls['selectedUser'].clearValidators();
        this.form.controls['selectedUser'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['firstName'].setValidators([Validators.required]);
        this.form.controls['lastName'].setValidators([Validators.required]);
        this.form.controls['email'].setValidators([
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
        ]);
        this.form.controls['phone'].setValidators([possiblePhoneValidator]);
        this.form.controls['firstName'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['lastName'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['email'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['phone'].updateValueAndValidity({ emitEvent: false });
        return;
      }
      this.isAddNewMode = false;
      if (userId && this.userOptions?.length > 0) {
        const selectedUser = this.userOptions.find(u => u?.value === userId || u?.uuid === userId || u?.id === userId);
        if (selectedUser) {
          this.selectedUserData = selectedUser;
          this.form.patchValue(
            {
              firstName: selectedUser?.first_name || null,
              lastName: selectedUser?.last_name || null,
              email: selectedUser?.email || null,
              phone: selectedUser?.phone || null,
            },
            { emitEvent: false }
          );
        }
      } else {
        this.selectedUserData = null;
      }
    });

    this.store
      .select(projectRolesListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && data?.list?.length > 0) {
          this.rolesOptions = [...data?.list];
          if (this.form.controls['role'].value) {
            this.updateSitesDropdownVisibility(this.form.controls['role'].value);
          }
        }
      });

    this.store
      .select(existingMembersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && data?.list?.length > 0) {
          this.userOptions = data.list.filter((user: any) => this.canManageOrgLevelAccount(user));
        }
      });

    this.store
      .select(projectMembersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projectMembers = data?.list ? [...data.list] : [];
        this.updateReportsToOptions(this.form.controls['role'].value);
      });

    this.store
      .select(projectSitesForInvitation)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && data?.length > 0) {
          this.sitesOptions = [...data];
          this.populateSitesFromData();
        }
      });

    this.form.controls['role'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(roleCode => {
      this.updateSitesDropdownVisibility(roleCode);
      this.updateReportsToOptions(roleCode);
    });

    if (this.form.controls['role'].value) {
      this.updateSitesDropdownVisibility(this.form.controls['role'].value);
      this.updateReportsToOptions(this.form.controls['role'].value);
    }

    this.applyReadOnlyState();
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  fetchProjectRolesList(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchProjectRolesListRequest({ payload }));
    }
  }

  fetchProjectMembersList(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchProjectMembersListRequest({ payload }));
    }
  }

  submit(): void {
    if (this.isReadOnly) {
      return;
    }

    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const values = this.form.getRawValue() as any;
    let payload: any;

    const sites = this.form.controls['sites'] ? values?.sites || [] : [];

    if (!this.canManageOrgLevelAccount(this.selectedUserData || this.data)) {
      this.commonService.notification('You are not allowed to manage this org-level project membership', 'danger');
      return;
    }

    let formattedPhone = values?.phone || '';
    if (formattedPhone && !formattedPhone.startsWith('+')) {
      formattedPhone = '+1' + formattedPhone;
    } else if (!formattedPhone || formattedPhone === '+1') {
      formattedPhone = null;
    }

    if (this.data?.id || this.data?.uuid) {
      payload = {
        projectId: this.projectId,
        accountId: this.data?.id || this.data?.uuid,
        first_name: values?.firstName,
        last_name: values?.lastName,
        role: values?.role,
        sites: sites,
        reports_to_account_id: this.isDwrReportingHierarchyEnabled ? values?.reportsTo || null : undefined,
        ...(values?.email && { email: values.email }),
        ...(formattedPhone && { phone: formattedPhone }),
      };
      this.store.dispatch(UpdateUserRequest({ payload }));
    } else {
      if (values?.selectedUser && values?.selectedUser !== 'ADD_NEW') {
        payload = {
          projectId: this.projectId,
          account_id: values?.selectedUser,
          role: values?.role,
          sites: sites,
          reports_to_account_id: this.isDwrReportingHierarchyEnabled ? values?.reportsTo || null : undefined,
        };
        this.store.dispatch(InviteUserRequest({ payload }));
      } else {
        if (!values?.email && !formattedPhone) {
          this.form.controls['email'].setErrors({ emailOrPhoneRequired: true });
          this.form.controls['email'].markAsTouched();
          this.form.controls['phone'].setErrors({ emailOrPhoneRequired: true });
          this.form.controls['phone'].markAsTouched();
          this.checkForErrors('email');
          this.checkForErrors('phone');
          return;
        }
        payload = {
          projectId: this.projectId,
          first_name: values?.firstName,
          last_name: values?.lastName,
          role: values?.role,
          sites: sites,
          reports_to_account_id: this.isDwrReportingHierarchyEnabled ? values?.reportsTo || null : undefined,
          ...(values?.email && { email: values.email }),
          ...(formattedPhone && { phone: formattedPhone }),
        };
        this.store.dispatch(InviteUserRequest({ payload }));
      }
    }
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField);
    if (currentField) {
      if (validationErrors && validationErrors[currentField]) {
        this.errorMessages = {
          ...this.errorMessages,
          ...validationErrors,
        };
      } else {
        const updatedErrorMessages = { ...this.errorMessages };
        delete updatedErrorMessages[currentField];
        this.errorMessages = updatedErrorMessages;
      }
    } else {
      if (validationErrors) {
        this.errorMessages = {
          ...this.errorMessages,
          ...validationErrors,
        };
      }
    }
  }

  onEmailBlur(): void {
    if (this.selectedUserData) {
      return;
    }
    const email = this.form.controls['email'].value;
    if (!email) {
      return;
    }
    const trimmedEmail = email.trim();
    if (!this.isValidEmail(trimmedEmail)) {
      this.form.controls['email'].markAsTouched();
      this.checkForErrors('email');
      return;
    }
    if (this.originalEmail && trimmedEmail === this.originalEmail) {
      return;
    }
    if (this.lastCheckedEmail && trimmedEmail === this.lastCheckedEmail) {
      return;
    }
    this.lastCheckedEmail = trimmedEmail;
    this.store.dispatch(CheckEmailExistsRequest({ payload: { email: trimmedEmail } }));
  }

  onPhoneBlur(): void {
    if (this.selectedUserData) {
      return;
    }
    const phone = this.form.controls['phone'].value;
    if (!phone) {
      return;
    }
    if (this.form.controls['phone'].hasError('possiblePhone')) {
      this.form.controls['phone'].markAsTouched();
      this.checkForErrors('phone');
      return;
    }
    const formattedPhone = toE164(phone);
    if (this.originalPhone && formattedPhone === this.originalPhone) {
      return;
    }
    if (this.lastCheckedPhone && formattedPhone === this.lastCheckedPhone) {
      return;
    }
    this.lastCheckedPhone = formattedPhone;
    this.store.dispatch(CheckPhoneExistsRequest({ payload: { phone: formattedPhone } }));
  }

  fetchUsersList(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchExistingMembersListRequest({ payload }));
    }
  }

  updateSitesDropdownVisibility(roleCode: any): void {
    if (roleCode) {
      const isInspectorRole = roleCode?.toLowerCase()?.includes('inspector');

      if (isInspectorRole && this.projectId) {
        if (!this.form.controls['sites']) {
          (this.form as any).addControl('sites', new FormControl({ value: [], disabled: this.isReadOnly }));
        }
        // If sites are already loaded, populate them immediately
        if (this.sitesOptions.length > 0) {
          this.populateSitesFromData();
        }
        this.fetchProjectSites();
      } else {
        if (this.form.controls['sites']) {
          (this.form as any).removeControl('sites');
        }
      }
    } else {
      if (this.form.controls['sites']) {
        (this.form as any).removeControl('sites');
      }
    }
  }

  updateReportsToOptions(roleCode: any): void {
    if (!this.isDwrReportingHierarchyEnabled) {
      this.reportsToOptions = [];
      this.form.controls['reportsTo'].setValue(null, { emitEvent: false });
      return;
    }

    const currentAccountId = this.data?.uuid || this.data?.id || null;
    const allowedRoles = roleCode === 'project-manager'
      ? ['project-manager']
      : ['lead-inspector', 'project-manager'];

    this.reportsToOptions = (this.projectMembers || [])
      .filter((member: any) => allowedRoles.includes(member?.role_code || member?.role))
      .filter((member: any) => (member?.uuid || member?.id) !== currentAccountId)
      .map((member: any) => {
        const name = [member?.first_name, member?.last_name].filter(Boolean).join(' ') || member?.name;
        const roleName = member?.role_name || member?.roleName || member?.role_code || member?.role;
        return {
          ...member,
          label: roleName ? `${name} (${roleName})` : name,
          value: member?.uuid || member?.id,
        };
      });

    const selectedReportsTo = this.form.controls['reportsTo'].value;
    if (
      this.projectMembers.length > 0
      && selectedReportsTo
      && !this.reportsToOptions.some((option: any) => option.value === selectedReportsTo)
    ) {
      this.form.controls['reportsTo'].setValue(null);
    }
  }

  isOrgLevelAccount(account: any): boolean {
    const role = account?.account_role || account?.accountRole || account?.role;
    return typeof role === 'string' && role.startsWith('org-');
  }

  canManageOrgLevelAccount(account: any): boolean {
    if (!this.isOrgLevelAccount(account)) return true;
    const role = account?.account_role || account?.accountRole || account?.role;
    return this.manageableOrgRoles === null || this.manageableOrgRoles.includes(role);
  }

  fetchProjectSites(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchProjectSitesForInvitationRequest({ payload }));
    }
  }

  populateSitesFromData(): void {
    if (!this.form.controls['sites']) {
      return;
    }

    // If data.sites exists, use those sites (editing existing user with site assignments)
    if (this.data?.sites && Array.isArray(this.data.sites) && this.sitesOptions.length > 0) {
      const siteValues = this.data.sites.map((site: any) => site?.value || site?.id).filter(Boolean);
      if (siteValues.length > 0) {
        this.form.controls['sites'].setValue(siteValues, { emitEvent: false });
      }
    }
    // Otherwise, select all sites by default when inspector role is selected
    else if (this.sitesOptions.length > 0) {
      const allSiteValues = this.sitesOptions.map((site: any) => site?.value || site?.id || site?.uuid).filter(Boolean);
      if (allSiteValues.length > 0) {
        this.form.controls['sites'].setValue(allSiteValues, { emitEvent: false });
      }
    }
  }

  applyReadOnlyState(): void {
    if (!this.isReadOnly) {
      return;
    }

    this.form.disable({ emitEvent: false });
    this.isFormDirty = false;
    this.projectsService.isAnyFormDirty.next(false);
    this.cdRef.detectChanges();
  }

  deleteUser(): void {
    if (this.isReadOnly) {
      return;
    }

    const userName =
      this.data?.first_name && this.data?.last_name
        ? `${this.data?.first_name} ${this.data?.last_name}`
        : this.data?.name || 'this user';
    const message = `Deleting <b>${userName}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          projectId: this.projectId,
          accountId: this.data?.id || this.data?.uuid,
        };
        this.store.dispatch(DeleteUserRequest({ payload }));
      }
    });
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
